import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import databaseService from '../services/DatabaseService';
import searchService from '../services/SearchService';

// Create the context
const SearchContext = createContext();

// Custom hook to use the search context
export function useSearch() {
  return useContext(SearchContext);
}

// Provider component
export function SearchProvider({ children }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [databaseIsLoading, setDatabaseIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeView, setActiveView] = useState('search'); // Default to search view
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [searchLog, setSearchLog] = useState('');
  const [fullPaper, setFullPaper] = useState(null);
  
  // Ref to the search input for focus management
  const searchInputRef = useRef(null);

  // Function to focus the search input
  const focusSearchInput = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Preload the search index when the component mounts
  useEffect(() => {
    const preloadDatabase = async () => {
      setDatabaseIsLoading(true);
      try {
        await databaseService.loadDatabase();
        // Load paper 0 as the default fullPaper
        const { results: initialPaperText, searchLog } = await searchService.search('[0]');
        console.log('Log:', searchLog);
        setFullPaper(initialPaperText);
      } catch (err) {
        console.error('Failed to preload database:', err);
        // Don't set error state here - we'll just retry when the user searches
      } finally {
        setDatabaseIsLoading(false);
      }
    };

    preloadDatabase();
  }, []); // Empty dependency array ensures this runs only once on mount

  // Function to perform search using the service
  const performSearch = async (fullUserQuery) => {
    if (!fullUserQuery.trim()) return;
    
    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    
    try {
      // Use the search service instead of implementing search logic here
      const { success, errors, results, isPureTextResult, totalCount, searchLog } = await searchService.search(fullUserQuery);
      if (success) {
        if (isPureTextResult) {
          setFullPaper(results);
          setActiveView('book');
        } else {
          setResults(results);
          setTotalSearchResults(totalCount);
          setSearchLog(searchLog);
          setActiveView('results');
        }
      } else {
        setError(errors.join(', '));
        setResults([]);
        setTotalSearchResults(0);
        setSearchLog(searchLog);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        throw err;
      }
      setError(err.message);
      setResults([]);
      setTotalSearchResults(0);
      setSearchLog(searchLog);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    searchTerm,
    setSearchTerm,
    results,
    isLoading,
    error,
    databaseIsLoading,
    performSearch,
    hasSearched,
    activeView,
    setActiveView,
    totalSearchResults,
    searchLog,
    searchInputRef,
    focusSearchInput,
    fullPaper,
    setFullPaper,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}
